import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { makeStyles, Theme } from '@material-ui/core/styles'
import clsx from 'clsx'
import { ProjectSettings } from 'models/projectSettings'
import {
    formatSection,
    hLevelChar,
    indent,
} from 'pages/PreviewPage/PreviewContent'
import { getDisplayText } from 'pages/ProjectsPage/WorkspacePage/components/Common/ContentItemElementText'
import { DRAGGABLE_ITEM_BORDER_RADIUS } from 'pages/ProjectsPage/WorkspacePage/components/Common/DraggableItem'
import {
    getIsElementHaveUnits,
    updateTextOfIPSIUnit,
} from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import PropertyEditorEditableText from 'pages/ProjectsPage/WorkspacePage/components/PropertyEditor/PropertyEditorEditableText'
import PropertyEditorOptionItem from 'pages/ProjectsPage/WorkspacePage/components/PropertyEditor/PropertyEditorOptionItem'
import SpecifierNote from 'pages/ProjectsPage/WorkspacePage/components/SpecifierNote'
import React from 'react'
import { v4 as newGuid } from 'uuid'

const forbiddenPreviewParagraphCharacters = ['|']

const useStyles = makeStyles((theme: Theme) => ({
    data: {
        display: 'inline',
    },
    notDisabledBoxedInputContainer: {
        border: '1px solid #F78909',
        color: theme.palette.common.black,
    },

    missingdata: {
        display: 'inline',
    },
    IP: {
        color: 'rgba(255, 0, 0, 1)',
    },
    SI: {
        color: 'rgba(0, 128, 128, 1)',
    },

    row: {
        display: 'table-row',
        marginTop: theme.spacing(1.5),
    },

    cell: {
        display: 'table-cell',
    },
    container: {
        paddingBottom: theme.spacing(1),
        borderRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
    },
    PRspace: { marginLeft: theme.spacing(1) },
    PR1: {
        marginLeft: theme.spacing(3),
    },
    PR2: {
        marginLeft: theme.spacing(5),
    },
    PR3: {
        marginLeft: theme.spacing(10),
    },
    PR4: {
        marginLeft: theme.spacing(13.5),
    },
    PR5: {
        marginLeft: theme.spacing(17),
    },
    editableText: {
        padding: theme.spacing(0.4375, 1),
        borderRadius: '',
        minWidth: 20,
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
        '&[disabled]': {
            color: '#40464F',
        },
        lineHeight: '30px',
    },

    editableTextPlain: {
        outline: 'none',
        paddingLeft: 0,
        paddingRight: theme.spacing(0.4375),
        paddingTop: 0,
        lineHeight: '30px',
    },
    unselectedOptionItem: {
        padding: theme.spacing(0.5, 1),
        color: '#F78909',
        background: '#FFF9F2',
    },
    optionItem: {
        border: '1px solid #F78909',
        borderRadius: '100px',
        '&:disabled': {
            color: '#40464F',
            border: '1px solid #40464F',
            background: theme.palette.common.white,
        },
    },
    optionGroupPreview: {
        margin: theme.spacing(0.25),
        fontWeight: 'normal',
    },
}))

export interface ImportPreviewContentProps {
    data?: Partial<TextContentElement>[]
    parentCount: number
    hierarchicalLevel?: number
    settings?: Partial<ProjectSettings>
    isDataFromArticle?: boolean
}
const ImportPreviewContent: React.FC<ImportPreviewContentProps> = ({
    data,
    parentCount,
    hierarchicalLevel,
    settings,
    isDataFromArticle,
}) => {
    const classes = useStyles()
    let content = [<div></div>]
    const unitsOfMeasure = settings?.unitOfMeasure
    const setSelectionOption = (
        eachElement: Partial<TextContentElement>,
        style: string,
        optedIn: boolean,
        settings?: Partial<ProjectSettings>
    ) => {
        const unitOfMeasure = settings?.unitOfMeasure
            ? settings?.unitOfMeasure.toString()
            : 'BOTH'
        const familyNumberFormat = settings?.familyNumberFormat
            ? settings?.familyNumberFormat
            : 'XXXXXX'
        const isBothUnitsActive = unitOfMeasure === 'BOTH'
        const isImperialUnitsActive = unitOfMeasure === 'IMPERIAL'
        const isMetricUnitsActive = unitOfMeasure === 'METRIC'
        const isElementHaveUnits = getIsElementHaveUnits(eachElement)
        const isShowImperial =
            isElementHaveUnits && (isBothUnitsActive || isImperialUnitsActive)
        const isShowMetric =
            isElementHaveUnits && (isBothUnitsActive || isMetricUnitsActive)
        const displayText = getDisplayText(
            eachElement,
            isShowMetric,
            isShowImperial,
            settings?.unitOfMeasure
        )
        if (
            eachElement.type === 'SelectionOption' &&
            eachElement.text !== '' &&
            displayText?.trim() === eachElement?.text?.trim()
        ) {
            content.push(
                optedIn ? (
                    <div className={clsx(style)}>
                        <PropertyEditorOptionItem
                            value={formatSection(
                                displayText!,
                                familyNumberFormat
                            )}
                            state={eachElement?.optionItemState}
                            data-testid={`selectionItem${eachElement?.id}`}
                            disabled={false}
                            calledFromImportPage
                        />
                    </div>
                ) : (
                    <div className={clsx(style)}>
                        <PropertyEditorOptionItem
                            value={formatSection(
                                displayText!,
                                familyNumberFormat
                            )}
                            state={eachElement?.optionItemState}
                            data-testid={`selectionItem${eachElement?.id}`}
                            disabled={false}
                            calledFromImportPage
                        />
                    </div>
                )
            )
        } else if (
            eachElement.type === 'SelectionOption' &&
            eachElement.text === '' &&
            displayText?.length !== 0
        ) {
            content.push(
                <div className={clsx(style)}>
                    <PropertyEditorOptionItem
                        value={formatSection(displayText!, familyNumberFormat)}
                        state={eachElement?.optionItemState}
                        data-testid={`selectionItem${eachElement?.id}`}
                        disabled={false}
                        calledFromImportPage
                    />
                </div>
            )
        }
        if (eachElement.type === 'ReplacementOption' && eachElement.text !== '')
            content.push(
                optedIn ? (
                    <div className={clsx(style)}>
                        &nbsp;
                        <PropertyEditorOptionItem
                            value={displayText}
                            state={eachElement?.optionItemState}
                            data-testid={`selectionItem${eachElement?.id}`}
                            disabled={false}
                            calledFromImportPage
                        />
                    </div>
                ) : (
                    <div className={clsx(style)}>
                        &nbsp;
                        {
                            <PropertyEditorEditableText
                                element={eachElement}
                                editable={false}
                                data-testid={`replacementOption${eachElement.id}`}
                                key={`editableText${eachElement.id}`}
                                value={displayText}
                                variant="boxed"
                                fieldStateType={eachElement.type}
                                isUserAdded={false}
                                calledFromImportPage
                            />
                        }
                    </div>
                )
            )
        if (
            (eachElement.type === 'SelectionOption' ||
                eachElement.type === 'ReplacementOption') &&
            eachElement.elements?.length &&
            eachElement.text === ''
        ) {
            if (
                eachElement.type === 'SelectionOption' &&
                eachElement.text === '' &&
                displayText?.length != 0
            )
                return
            eachElement.elements.map(
                (eachChildElement: Partial<TextContentElement>) => {
                    const isBothUnitsActive = unitOfMeasure === 'BOTH'
                    const isImperialUnitsActive = unitOfMeasure === 'IMPERIAL'
                    const isMetricUnitsActive = unitOfMeasure === 'METRIC'
                    const isElementHaveUnits = getIsElementHaveUnits(
                        eachElement
                    )
                    const isShowImperial =
                        isElementHaveUnits &&
                        (isBothUnitsActive || isImperialUnitsActive)
                    const isShowMetric =
                        isElementHaveUnits &&
                        (isBothUnitsActive || isMetricUnitsActive)
                    const childDisplayText = getDisplayText(
                        eachChildElement,
                        isShowMetric,
                        isShowImperial,
                        settings?.unitOfMeasure
                    )
                    switch (eachChildElement.type) {
                        case 'Text':
                            {
                                content.push(
                                    <div className={clsx(style)}>
                                        &nbsp;{childDisplayText}
                                    </div>
                                )
                            }
                            break
                        case 'UnitsOfMeasure':
                            const updateUOMElement = updateTextOfIPSIUnit(
                                eachChildElement,
                                isShowMetric,
                                isShowImperial,
                                settings?.unitOfMeasure
                            )
                            const textForUnitOfMeasure = isBothUnitsActive
                                ? childDisplayText?.trim()?.length !== 0
                                    ? `${childDisplayText}(${updateUOMElement?.metricUnits})`
                                    : updateUOMElement?.metricUnits
                                : childDisplayText

                            const hidePill =
                                isImperialUnitsActive &&
                                eachChildElement?.imperialUnits?.trim()
                                    ?.length === 0
                                    ? true
                                    : isMetricUnitsActive &&
                                      eachChildElement?.metricUnits?.trim()
                                          ?.length === 0
                                    ? true
                                    : false

                            !hidePill &&
                                content.push(
                                    <>
                                        {eachElement?.type ===
                                        'SelectionOption' ? (
                                            <PropertyEditorOptionItem
                                                value={textForUnitOfMeasure}
                                                state={
                                                    eachElement?.optionItemState
                                                }
                                                data-testid={`selectionItem${eachElement?.id}`}
                                                disabled={false}
                                                calledFromImportPage
                                            />
                                        ) : (
                                            <PropertyEditorEditableText
                                                element={eachChildElement}
                                                editable={false}
                                                calledFromImportPage
                                                data-testid={
                                                    eachChildElement.type ===
                                                    'UnitsOfMeasure'
                                                        ? `inputElementText${eachChildElement.id}`
                                                        : `replacementOption${eachChildElement.id}`
                                                }
                                                identifier={
                                                    eachChildElement.type ===
                                                    'UnitsOfMeasure'
                                                        ? `inputElementText${
                                                              eachChildElement.id
                                                          }${
                                                              isShowMetric
                                                                  ? '-metric'
                                                                  : '-imperial'
                                                          }`
                                                        : `replacementOption${eachChildElement.id}`
                                                }
                                                key={`editableText${eachChildElement.id}`}
                                                additionalBlockedCharacters={
                                                    forbiddenPreviewParagraphCharacters
                                                }
                                                value={textForUnitOfMeasure}
                                                variant="boxed"
                                                isUserAdded={false}
                                            />
                                        )}
                                    </>
                                )
                            break
                    }
                }
            )
        }
    }

    data?.map((each: Partial<TextContentElement>, eachIndex: number) => {
        switch (each?.type) {
            case 'Text':
            case 'SectionNumber':
            case 'SectionName':
                content.push(
                    <div
                        className={clsx(
                            classes.data,
                            classes.container,
                            classes.editableText,
                            classes.editableTextPlain
                        )}
                    >
                        {each.text?.replace(/(-\d+)(?=\s*:|$)/g, '')}
                    </div>
                )
                break
            case 'Comment':
                content.push(
                    <GridItem className="notes">
                        <SpecifierNote
                            key={`previewNote${each.id}`}
                            notes={[each?.text!]}
                            isHidden={false}
                            calledFromImport
                        />
                    </GridItem>
                )
                break
            case 'ReplacementOption':
                content.push(
                    <PropertyEditorEditableText
                        element={each}
                        editable={false}
                        data-testid={`replacementOption${each.id}`}
                        key={`editableText${each.id}`}
                        value={each?.text}
                        variant="boxed"
                        isUserAdded={false}
                    />
                )
                break
            case 'UnitsOfMeasure':
                let unitOfMeasure = settings?.unitOfMeasure
                    ? settings?.unitOfMeasure
                    : 'BOTH'
                const isBothUnitsActive = unitOfMeasure === 'BOTH'
                const isImperialUnitsActive = unitOfMeasure === 'IMPERIAL'
                const isMetricUnitsActive = unitOfMeasure === 'METRIC'
                const isElementHaveUnits =
                    each.type === 'UnitsOfMeasure' ||
                    ((each.type === 'SelectionOption' ||
                        each.type === 'ReplacementOption') &&
                        each.elements?.length! > 0 &&
                        each.elements?.some(
                            (element) =>
                                element.imperialUnits !== '' ||
                                element.metricUnits !== ''
                        ))
                const isShowImperial =
                    isElementHaveUnits &&
                    (isBothUnitsActive || isImperialUnitsActive)
                const isShowMetric =
                    isElementHaveUnits &&
                    (isBothUnitsActive || isMetricUnitsActive)
                const displayText = getDisplayText(
                    each,
                    isShowMetric,
                    isShowImperial,
                    settings?.unitOfMeasure
                )
                const updateUOMElement = updateTextOfIPSIUnit(
                    each,
                    isShowMetric,
                    isShowImperial,
                    settings?.unitOfMeasure
                )
                const textForUnitOfMeasure = isBothUnitsActive
                    ? displayText?.trim()?.length !== 0
                        ? `${displayText} (${updateUOMElement?.metricUnits})`
                        : updateUOMElement?.metricUnits
                    : displayText

                const hidePill =
                    isImperialUnitsActive &&
                    each?.imperialUnits?.trim()?.length === 0
                        ? true
                        : isMetricUnitsActive &&
                          each?.metricUnits?.trim()?.length === 0
                        ? true
                        : false

                !hidePill &&
                    content.push(
                        <>
                            <PropertyEditorEditableText
                                element={each}
                                editable={false}
                                data-testid={
                                    each.type === 'UnitsOfMeasure'
                                        ? `inputElementText${each.id}`
                                        : `replacementOption${each.id}`
                                }
                                identifier={
                                    each.type === 'UnitsOfMeasure'
                                        ? `inputElementText${each.id}${
                                              isShowMetric
                                                  ? '-metric'
                                                  : '-imperial'
                                          }`
                                        : `replacementOption${each.id}`
                                }
                                key={`editableText${each.id}`}
                                additionalBlockedCharacters={
                                    forbiddenPreviewParagraphCharacters
                                }
                                value={textForUnitOfMeasure}
                                variant="boxed"
                                calledFromImportPage
                                isUserAdded={false}
                            />
                        </>
                    )

                break
            case 'OptionGroup':
                if (each.elements && each.elements?.length > 0) {
                    each.elements.map(
                        (eachElement: Partial<TextContentElement>) => {
                            setSelectionOption(
                                eachElement,
                                !isDataFromArticle
                                    ? clsx(
                                          classes.missingdata,
                                          classes.optionGroupPreview
                                      )
                                    : clsx(
                                          classes.data,
                                          classes.optionGroupPreview
                                      ),
                                false,
                                settings
                            )
                        }
                    )
                }
                break
            case 'SelectionOption':
                setSelectionOption(
                    each,
                    !isDataFromArticle
                        ? clsx(classes.missingdata, classes.optionGroupPreview)
                        : clsx(classes.data, classes.optionGroupPreview),
                    false,
                    settings
                )
                break
            case 'Hyperlink':
                // split property key from the rest of the content
                let hyperlinkContent = each.text
                let hyperlinkPropertyKey = ''
                const firstHyperlinkColonIndex = each.text?.indexOf(':') ?? -1
                if (eachIndex === 0) {
                    hyperlinkPropertyKey =
                        each?.text?.substring(
                            0,
                            firstHyperlinkColonIndex >= 0
                                ? firstHyperlinkColonIndex + 1
                                : 0
                        ) ?? ''
                    hyperlinkContent = each.text?.substring(
                        firstHyperlinkColonIndex >= 0
                            ? firstHyperlinkColonIndex + 1
                            : 0
                    )
                }
                content.push(
                    <div className={clsx(classes.missingdata)}>
                        <PropertyEditorEditableText
                            element={each}
                            editable={false}
                            value={hyperlinkContent}
                            titleText={hyperlinkPropertyKey}
                            data-testid={`hyperlink${each.id}`}
                            identifier={`hyperlink${each.id}`}
                            additionalBlockedCharacters={
                                forbiddenPreviewParagraphCharacters
                            }
                            variant="plain"
                            isUserAdded={false}
                        />
                    </div>
                )

                break
        }
    })
    return (
        <div className={classes.row}>
            {hierarchicalLevel && hierarchicalLevel != 1 ? (
                <div className={clsx(classes.cell)}>
                    {indent(hierarchicalLevel, classes)}
                    {hLevelChar(hierarchicalLevel, parentCount)}
                    {hierarchicalLevel && hierarchicalLevel >= 2 ? (
                        <span className={classes.PRspace}></span>
                    ) : (
                        ''
                    )}
                </div>
            ) : (
                ''
            )}
            <div className={clsx(classes.cell)}>
                {content.map((element) => (
                    <div
                        data-testid="PrintPreviewElements"
                        className={clsx(classes.data)}
                        key={newGuid()}
                    >
                        {element}
                    </div>
                ))}
            </div>
        </div>
    )
}

export default ImportPreviewContent
